/* eslint-disable no-useless-escape */
import React, { useEffect } from 'react';
import {
    ICustomColor,
    IOrderDetails,
    IProductDetail,
    StateResult,
    createStateResult,
    IProductHoverImage,
    OptionTypes,
} from '../../../models/product-detail';
import { findOption } from './rules-engine';
import { getCustomColorOptions, prepareCustomColors } from './product-detail';
import { getRelatedItems } from '../../../api/product-api';

export const prepareCustomImageUrl = (
    baseUrl: string,
    faceplates: ICustomColor[],
    shells: ICustomColor[],
    facePlateObject?: ICustomColor,
    shellObject?: ICustomColor
) => {
    return faceplates.length > 0 && shells.length > 0
        ? `${baseUrl}-${
              facePlateObject
                  ? facePlateObject.colorName
                  : faceplates[0].colorName
          }-${
              shellObject ? shellObject.colorName : shells[0].colorName
          }${'.ashx'}`
        : `${baseUrl}`;
};

interface Props {
    orderState: IOrderDetails;
    productDetails: IProductDetail;
    useShellsForCustom?: boolean;
    name: string;
    faceplates: ICustomColor[];
    shells: ICustomColor[];
    onChange: (stateResult: StateResult) => void;
    onHover: (productImage?: IProductHoverImage) => void;
}

const ColorSelector: React.FC<Props> = ({
    orderState,
    productDetails,
    name,
    faceplates,
    shells,
    onChange,
    onHover,
}) => {
    // TODO move faceplates / shells and image url generation to productDetail so we can update the custom faceplate / shells on load and update (without use Effect)
    const isShellCompatible = (
        faceplate: ICustomColor | undefined,
        shell: ICustomColor | undefined
    ): boolean => {
        if (!shell) return true;
        const shellOpt = findOption(shell.id, productDetails.options);
        return (
            !faceplate ||
            !shellOpt ||
            !shellOpt.incompatible ||
            (shellOpt.incompatible.showIf &&
                shellOpt.incompatible.showIf.some((x) => x === faceplate.id)) ||
            (shellOpt.incompatible.hidesFrom &&
                shellOpt.incompatible.hidesFrom.every(
                    (x) => x !== faceplate.id
                ))
        );
    };

    const shellOptions = () => {
        return prepareCustomColors(
            getCustomColorOptions(
                productDetails.options,
                OptionTypes.ShellColor
            ),
            orderState.side,
            true
        );
    };

    const handleMouseEnter = (id: string) => {
        let facePlateObject: ICustomColor | undefined;
        let shellObject: ICustomColor | undefined;
        let imageUrl = '';
        let color = '';
        let shellColor = '';
        let partNumber = '';

        switch (name) {
            case 'ColorSelection': {
                const standardColor = productDetails.standardColors.find(
                    (x) => x.color === id.toString()
                )!;
                imageUrl = standardColor.imageUrl;
                color = standardColor.color;
                partNumber = standardColor.partNumber;
                break;
            }
            case 'CustomColorPlate':
                facePlateObject = faceplates.find((x) => x.id === +id);

                shellObject = shells.find((x) =>
                    orderState.rightSelectedIds.includes(x.id)
                );

                if (!isShellCompatible(facePlateObject, shellObject)) {
                    shellObject = shellOptions().find((shell) =>
                        isShellCompatible(facePlateObject, shell)
                    );
                    shellColor = shellObject!.colorName;
                }
                imageUrl = prepareCustomImageUrl(
                    productDetails.imageBase,
                    faceplates,
                    shells,
                    facePlateObject,
                    shellObject
                );
                color = facePlateObject!.colorName;
                break;
            case 'CustomColorShell':
                facePlateObject = faceplates.find((x) =>
                    orderState.rightSelectedIds.includes(x.id)
                );

                shellObject = shells.find((x) => x.id === +id);

                imageUrl = prepareCustomImageUrl(
                    productDetails.imageBase,
                    faceplates,
                    shells,
                    facePlateObject,
                    shellObject
                );
                shellColor = shellObject!.colorName;
                break;
            default:
                break;
        }

        onHover({ imageUrl, color, partNumber, shellColor });
    };

    const handleMouseOut = () => {
        onHover(undefined);
    };

    const handleChange = (event: any) => {
        const { value } = event.target;
        const rightOptions = orderState!.rightSelectedIds.concat([]);
        const leftOptions = orderState!.leftSelectedIds.concat([]);

        const stateResult = createStateResult();

        switch (name) {
            case 'ColorSelection': {
                const standardColorObject = productDetails.standardColors.find(
                    (x) => x.color === event.target.value
                );

                const standardFaceplates = productDetails!.options
                    .find((x) => x.id === 10)!
                    .items.find((y) => y.id === 5)!.items;

                standardFaceplates.forEach((x) => {
                    if (rightOptions.includes(x.id)) {
                        rightOptions.splice(rightOptions.indexOf(x.id), 1);
                    }
                    if (leftOptions.includes(x.id)) {
                        leftOptions.splice(leftOptions.indexOf(x.id), 1);
                    }
                });

                rightOptions.push(
                    standardFaceplates.find(
                        (x) => x.english === standardColorObject!.color
                    )!.id
                );

                leftOptions.push(
                    standardFaceplates.find(
                        (x) => x.english === standardColorObject!.color
                    )!.id
                );

                getRelatedItems(standardColorObject!.partNumber).then(
                    (relatedItems) => {
                        stateResult.productDetails = {
                            ...stateResult.productDetails!,
                            imageUrl: standardColorObject!.imageUrl,
                            partNumber: standardColorObject!.partNumber,
                            color: standardColorObject!.color,
                            accessories: relatedItems.data!.accessories,
                            commonSupplies: relatedItems.data!.commonSupplies,
                        };
                        onChange(stateResult);
                    }
                );

                stateResult.productDetails = {
                    ...stateResult.productDetails!,
                    imageUrl: standardColorObject!.imageUrl,
                    partNumber: standardColorObject!.partNumber,
                    color: standardColorObject!.color,
                };

                stateResult.orderDetails = {
                    ...stateResult.orderDetails!,
                    selectedColor: standardColorObject!.color,
                    partNumber: standardColorObject!.partNumber,
                    selectedItemNumber: standardColorObject!.partNumber,
                    selectedImage: standardColorObject!.imageUrl,
                    itemGuid: standardColorObject!.itemGuid!,
                    rightSelectedIds: rightOptions,
                    leftSelectedIds: leftOptions,
                };

                break;
            }
            case 'CustomColorPlate': {
                faceplates.forEach((x) => {
                    if (x.id === +value) {
                        rightOptions.push(+value);
                        leftOptions.push(+value);
                    } else {
                        if (rightOptions.indexOf(x.id) >= 0)
                            rightOptions.splice(rightOptions.indexOf(x.id), 1);
                        if (leftOptions.indexOf(x.id) >= 0)
                            leftOptions.splice(leftOptions.indexOf(x.id), 1);
                    }
                });

                stateResult.orderDetails = {
                    ...stateResult.orderDetails!,
                    rightSelectedIds: rightOptions,
                    leftSelectedIds: leftOptions,
                };
                break;
            }
            case 'CustomColorShell': {
                shells.forEach((x) => {
                    if (x.id === +value) {
                        rightOptions.push(+value);
                        leftOptions.push(+value);
                    } else {
                        if (rightOptions.indexOf(x.id) >= 0)
                            rightOptions.splice(rightOptions.indexOf(x.id), 1);
                        if (leftOptions.indexOf(x.id) >= 0)
                            leftOptions.splice(leftOptions.indexOf(x.id), 1);
                    }
                });

                stateResult.orderDetails = {
                    ...stateResult.orderDetails!,
                    rightSelectedIds: rightOptions,
                    leftSelectedIds: leftOptions,
                };
                break;
            }
            default:
                break;
        }

        onChange(stateResult);
    };

    const getCustomSwatches = () => {
        return (name === 'CustomColorShell' ? shells : faceplates) || [];
    };

    useEffect(() => {
        if (orderState && faceplates.length > 0 && shells.length > 0) {
            try {
                const stateResult = createStateResult();

                const facePlateObject: ICustomColor | undefined =
                    faceplates.find((x) =>
                        orderState.rightSelectedIds.includes(x.id)
                    );

                const shellObject: ICustomColor | undefined = shells.find((x) =>
                    orderState.rightSelectedIds.includes(x.id)
                );

                const imageUrl = prepareCustomImageUrl(
                    productDetails.imageBase,
                    faceplates,
                    shells,
                    facePlateObject,
                    shellObject
                );

                stateResult.orderDetails = {
                    ...stateResult.orderDetails!,
                    selectedImage: imageUrl,
                };

                stateResult.productDetails!.imageUrl = imageUrl;
                stateResult.productDetails!.faceplateColor =
                    facePlateObject!.colorName;
                stateResult.productDetails!.color = facePlateObject!.colorName;
                stateResult.productDetails!.shellColor = shellObject!.colorName;

                onChange(stateResult);
                onHover(undefined);
            } catch {
                return;
            }
        }
    }, [faceplates, shells]);

    return (
        <div id="color-selector-div">
            {productDetails.isCustom && orderState.rightSelectedIds ? (
                <>
                    {getCustomSwatches().map((option) => {
                        return (
                            <p
                                className="color-select"
                                key={option.id}
                                id={option.colorName}
                            >
                                <input
                                    id={option.id.toString()}
                                    className="pdp-select-button"
                                    type="radio"
                                    name={option.id.toString()}
                                    value={option.id}
                                    checked={
                                        (orderState.side !== 'L' &&
                                            orderState.rightSelectedIds.includes(
                                                option.id
                                            )) ||
                                        (orderState.side !== 'R' &&
                                            orderState.leftSelectedIds.includes(
                                                option.id
                                            ))
                                    }
                                    onChange={handleChange}
                                />
                                <label
                                    id={`label-${option.id}`}
                                    className="color-label"
                                    htmlFor={option.id.toString()}
                                    onMouseEnter={() =>
                                        handleMouseEnter(option.id.toString())
                                    }
                                    onMouseOut={handleMouseOut}
                                >
                                    <img
                                        src={option.swatchUrl}
                                        alt={option.colorName}
                                        style={{
                                            height: '45px',
                                            width: '45px',
                                            marginLeft: '3px',
                                            marginTop: '3px',
                                            pointerEvents: 'none',
                                        }}
                                    />
                                </label>
                            </p>
                        );
                    })}
                </>
            ) : (
                <>
                    {productDetails.standardColors.map((option) => {
                        return (
                            <p className="color-select" key={option.color}>
                                <input
                                    type="radio"
                                    id={option.color}
                                    value={option.color}
                                    checked={
                                        orderState.selectedColor ===
                                        option.color
                                    }
                                    onChange={handleChange}
                                />
                                <label
                                    id={`label-${option.color}`}
                                    className="color-label"
                                    htmlFor={option.color}
                                    onMouseEnter={() =>
                                        handleMouseEnter(option.color)
                                    }
                                    onMouseOut={handleMouseOut}
                                >
                                    <img
                                        src={option.swatchUrl}
                                        alt={option.color}
                                        style={{
                                            height: '45px',
                                            width: '45px',
                                            marginLeft: '3px',
                                            marginTop: '3px',
                                            pointerEvents: 'none',
                                        }}
                                    />
                                </label>
                            </p>
                        );
                    })}
                </>
            )}
        </div>
    );
};

export default ColorSelector;
